/**
 * Application Utility Functions
 * 
 * ⚠️ CRITICAL: These utilities are SEPARATE from existing questionnaire utils
 * Supports the new unauthenticated applications/ collection
 */

import { Timestamp } from 'firebase/firestore';
import { 
  ApplicationDocument, 
  ApplicationFormData, 
  ApplicationStatus,
  ReviewStatus,
  ApplicationSource,
  QuestionnaireResponses,
  ResponseSection,
  ApplicationSession,
  ApplicationMetrics
} from '../types/application.types';

// Session management utilities
export class ApplicationSessionManager {
  private static readonly SESSION_KEY = 'voluntas_application_session';
  
  static generateSessionId(): string {
    return `app_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  static saveSession(session: ApplicationSession): void {
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify({
        ...session,
        lastActiveAt: Date.now() // Store as timestamp for localStorage
      }));
    } catch (error) {
      // Failed to save application session
      throw new Error(`Failed to save application session: ${error}`);
    }
  }
  
  static getSession(): ApplicationSession | null {
    try {
      const stored = localStorage.getItem(this.SESSION_KEY);
      if (!stored) return null;
      
      const session = JSON.parse(stored);
      // Convert timestamp back to Firestore Timestamp
      session.lastActiveAt = Timestamp.fromMillis(session.lastActiveAt);
      
      return session;
    } catch (error) {
      // Failed to retrieve application session
      throw new Error(`Failed to retrieve application session: ${error}`);
    }
  }
  
  static clearSession(): void {
    try {
      localStorage.removeItem(this.SESSION_KEY);
    } catch (error) {
      // Failed to clear application session
      throw new Error(`Failed to clear application session: ${error}`);
    }
  }
  
  static isSessionExpired(session: ApplicationSession, timeoutMinutes: number = 60): boolean {
    const now = Timestamp.now();
    const timeDiff = now.toMillis() - session.lastActiveAt.toMillis();
    return timeDiff > (timeoutMinutes * 60 * 1000);
  }
}

// Application ID generation - moved to applicationManagement.service.ts

// Device fingerprinting utilities
export function generateDeviceFingerprint(components?: Record<string, any>): string {
  // Fallback if FingerprintJS not available
  if (!components) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('fingerprint', 2, 2);
    const canvasData = canvas.toDataURL();
    
    const fingerprint = btoa(JSON.stringify({
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : 'unknown',
      canvas: canvasData.slice(-100), // Last 100 chars of canvas data
      timestamp: Date.now()
    }));
    
    return fingerprint.slice(0, 64); // Limit length
  }
  
  // Use FingerprintJS components if available
  return btoa(JSON.stringify(components)).slice(0, 64);
}

// Application status utilities
export function getApplicationStatusColor(status: ApplicationStatus): string {
  switch (status) {
    case 'incomplete': return '#DFAA65'; // Warning amber -> design system warning-500
    case 'ai_processing': return '#0F335B'; // Blue -> design system primary-500
    case 'pending_review': return '#599DA7'; // Purple -> design system accent-500 (teal for pending)
    case 'submitted': return '#26543D'; // Green -> design system success-500
    default: throw new Error(`Unknown application status: ${status}`);
  }
}

export function getApplicationStatusLabel(status: ApplicationStatus): string {
  switch (status) {
    case 'incomplete': return 'In Progress';
    case 'ai_processing': return 'AI Processing';
    case 'pending_review': return 'Pending Review';
    case 'submitted': return 'Submitted';
    default: return 'Unknown';
  }
}

export function getPipelineDisplayStatus(params: {
  reviewStatus?: ReviewStatus | null;
  applicationStatus?: ApplicationStatus | null;
  salesStage?: string | null;
}): string {
  if (params.salesStage === 'Won') return 'Won';
  if (params.salesStage === 'Lost') return 'Lost';

  switch (params.reviewStatus) {
    case 'approved': return 'Won';
    case 'rejected': return 'Lost';
    case 'follow-up': return 'Follow-Up Required';
    case 'reviewing': return 'Under Review';
    default: break;
  }

  switch (params.applicationStatus) {
    case 'submitted': return 'Submitted';
    case 'pending_review': return 'Pending Review';
    case 'ai_processing': return 'AI Processing';
    case 'incomplete': return 'Incomplete';
    default: return 'Incomplete';
  }
}

export function getReviewStatusLabel(status: ReviewStatus): string {
  switch (status) {
    case 'unreviewed': return 'Awaiting Review';
    case 'reviewing': return 'Under Review';
    case 'approved': return 'Won';
    case 'rejected': return 'Lost';
    case 'follow-up': return 'Follow Up Required';
    default: return 'Unknown';
  }
}

// Form completion utilities
export function calculateSectionCompletion(section: ResponseSection & Record<string, any>): number {
  const fields = Object.keys(section).filter(key => 
    !['lastModified', 'aiGenerated', 'aiConfidence'].includes(key)
  );
  
  const completedFields = fields.filter(key => {
    const value = section[key];
    return value !== null && value !== undefined && value !== '';
  });
  
  return fields.length > 0 ? (completedFields.length / fields.length) * 100 : 0;
}

export function calculateOverallCompletion(responses: QuestionnaireResponses): number {
  const sections = Object.values(responses);
  const sectionCompletions = sections.map(section => calculateSectionCompletion(section));
  const average = sectionCompletions.reduce((sum, completion) => sum + completion, 0) / sections.length;
  return Math.round(average);
}

export function getRequiredFields(): Record<string, string[]> {
  return {
    applicantInfo: ['fullName', 'emailAddress', 'companyName', 'companyWebsite'],
    businessFundamentals: ['singleSentenceExplanation', 'problemSolved'],
    marketAndIndustry: ['totalAddressableMarket', 'customerSegments'],
    revenueAndBusinessModel: ['revenueGeneration', 'revenueModel'],
    // technologyAndDefensibility removed - fields not extracted by AI
    growthAndInvestment: ['scalingStrategy', 'fundingAmountSought']
  };
}

export function validateRequiredFields(formData: Partial<ApplicationFormData>): Record<string, string[]> {
  const required = getRequiredFields();
  const missing: Record<string, string[]> = {};
  
  Object.entries(required).forEach(([section, fields]) => {
    const missingInSection = fields.filter(field => {
      const value = formData[field as keyof ApplicationFormData];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (missingInSection.length > 0) {
      missing[section] = missingInSection;
    }
  });
  
  return missing;
}

// Time tracking utilities
export function formatTimeSpent(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

export function trackSectionTime(sectionId: string, startTime: number): number {
  const timeSpent = Date.now() - startTime;
  
  // Store in session storage for persistence
  try {
    const existing = JSON.parse(sessionStorage.getItem('section_times') || '{}');
    existing[sectionId] = (existing[sectionId] || 0) + Math.round(timeSpent / 1000);
    sessionStorage.setItem('section_times', JSON.stringify(existing));
    return existing[sectionId];
  } catch (error) {
    // Failed to track section time
    throw new Error(`Failed to track section time: ${error}`);
  }
}

export function getSectionTimes(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem('section_times') || '{}');
  } catch (error) {
    // Failed to get section times
    throw new Error(`Failed to get section times: ${error}`);
  }
}

// File validation utilities
export function validateFile(file: File): { valid: boolean; error?: string } {
  const allowedTypes = ['application/pdf'];
  const maxSize = 50 * 1024 * 1024; // 50MB
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File must be PDF format' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 50MB' };
  }
  
  return { valid: true };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// AI confidence utilities
export function getConfidenceLabel(confidence: number | null): string {
  if (confidence === null) return 'Manual';
  if (confidence >= 0.8) return 'High Confidence';
  if (confidence >= 0.5) return 'Medium Confidence';
  return 'Low Confidence';
}

export function getConfidenceColor(confidence: number | null): string {
  if (confidence === null) return '#6B7280'; // Gray-500 (neutral - keep generic gray)
  if (confidence >= 0.8) return '#26543D'; // Success green -> design system success-500
  if (confidence >= 0.5) return '#DFAA65'; // Warning amber -> design system warning-500
  return '#D9534F'; // Error red -> design system error-500
}

// URL generation utilities
export function generateResumeUrl(sessionId: string, applicationId?: string): string {
  const params = new URLSearchParams({ session: sessionId });
  if (applicationId) params.set('app', applicationId);
  return `/apply?${params.toString()}`;
}

export function parseResumeUrl(url: string): { sessionId?: string; applicationId?: string } {
  const urlObj = new URL(url, window.location.origin);
  return {
    sessionId: urlObj.searchParams.get('session') || undefined,
    applicationId: urlObj.searchParams.get('app') || undefined
  };
}

// Analytics utilities
export function calculateApplicationMetrics(applications: ApplicationDocument[]): ApplicationMetrics {
  const total = applications.length;
  const completed = applications.filter(app => app.metadata.applicationStatus === 'submitted').length;
  const aiUsed = applications.filter(app => app.metadata.submissionMethod === 'ai_assisted').length;
  const qualified = applications.filter(app => app.metadata.reviewStatus === 'approved').length;
  
  const completionTimes = applications
    .filter(app => app.metadata.submittedAt && app.analyticsData.totalTimeSpentSeconds > 0)
    .map(app => app.analyticsData.totalTimeSpentSeconds);
  
  const averageTime = completionTimes.length > 0 
    ? completionTimes.reduce((sum, time) => sum + time, 0) / completionTimes.length 
    : 0;
  
  const abandonmentBySection: Record<string, number> = {};
  applications
    .filter(app => app.metadata.applicationStatus === 'incomplete')
    .forEach(app => {
      const section = app.metadata.abandonmentData.lastActiveSection;
      abandonmentBySection[section] = (abandonmentBySection[section] || 0) + 1;
    });
  
  const sourceDistribution: Record<ApplicationSource, number> = {
    website: 0,
    referral: 0,
    direct: 0,
    admin_lead: 0
  };
  applications.forEach(app => {
    sourceDistribution[app.metadata.source]++;
  });
  
  return {
    totalApplications: total,
    completionRate: total > 0 ? (completed / total) * 100 : 0,
    averageTimeToComplete: averageTime,
    aiUsageRate: total > 0 ? (aiUsed / total) * 100 : 0,
    qualificationRate: completed > 0 ? (qualified / completed) * 100 : 0,
    abandonmentBySection,
    sourceDistribution
  };
}

// Form data transformation utilities - REMOVED: Dead code referencing old 37-field structure

// Error handling utilities
export function getErrorMessage(error: any): string {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.code) return `Error: ${error.code}`;
  return 'An unexpected error occurred';
}

export function isRetryableError(error: any): boolean {
  const retryableCodes = ['unavailable', 'timeout', 'cancelled', 'deadline-exceeded'];
  return retryableCodes.includes(error?.code) || error?.retryable === true;
}

// Debug utilities
export const logApplicationDebugInfo = (app: ApplicationDocument): void => {
  // Application debug info logging disabled in production
  // Use proper logging service in development
};